import { ApiOperationType, AsoScenarioMinLayerResponse } from '@ydistri/api-sdk';
import {
  SignalAsoConfigurationChanged,
  SignalAsoScenarioScopeChanged,
  SignalrUpdate,
} from '../../signalr/signalrInterfaces';
import { signalrClient } from '../../signalr/client';
import { AsoCollection } from '../../swagger/collections';

const LAYER_CREATED = 'LayerCreated';
const LAYER_SCOPE_ID = 'LayerScopeId';
const LAYER_LIST_IDS = 'LayerListIds';
const LAYER_DELETED = 'LayerDeleted';
const SCOPE_CREATED = 'ScopeCreated';
const SCOPE_DELETED = 'ScopeDeleted';

interface AsoLayersCacheEntryApi {
  cacheDataLoaded: Promise<unknown>;
  cacheEntryRemoved: Promise<unknown>;
  updateCachedData: (recipe: (draft: AsoScenarioMinLayerResponse[]) => void) => void;
}

const parseSafeInteger = (value?: string): number | undefined => {
  if (value === undefined || value.trim() === '') return undefined;

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

const parseAsoLayerSignalValue = (
  value?: string,
): { layerId: number; value: string } | undefined => {
  if (value === undefined) return undefined;

  const [layerIdRaw, ...rest] = value.split(':');
  const layerId = parseSafeInteger(layerIdRaw);
  if (layerId === undefined) return undefined;

  return { layerId, value: rest.join(':') };
};

export const shouldReloadAsoLayersFromConfigurationSignalr = (updates: SignalrUpdate[]): boolean =>
  updates.some(update => update.fieldName === LAYER_CREATED);

export const shouldReloadAsoLayersFromScopeSignalr = (
  signal: SignalAsoScenarioScopeChanged,
): boolean => {
  return signal.updates.some(
    update => update.fieldName === SCOPE_CREATED || update.fieldName === SCOPE_DELETED,
  );
};

export const applyAsoLayerSignalrUpdate = (
  layers: AsoScenarioMinLayerResponse[],
  update: SignalrUpdate,
): void => {
  switch (update.fieldName) {
    case LAYER_SCOPE_ID: {
      const parsed = parseAsoLayerSignalValue(update.value);
      if (!parsed) return;

      const layer = layers.find(item => item.id === parsed.layerId);
      if (!layer) return;

      layer.scopeId = parseSafeInteger(parsed.value) ?? null;
      return;
    }

    case LAYER_LIST_IDS: {
      const parsed = parseAsoLayerSignalValue(update.value);
      if (!parsed) return;

      const layer = layers.find(item => item.id === parsed.layerId);
      if (!layer) return;

      layer.listIds =
        parsed.value === ''
          ? []
          : parsed.value
              .split(',')
              .map(parseSafeInteger)
              .filter(value => value !== undefined);
      return;
    }

    case LAYER_DELETED: {
      const layerId = parseSafeInteger(update.value);
      if (layerId === undefined) return;

      const layerIndex = layers.findIndex(item => item.id === layerId);
      if (layerIndex >= 0) {
        layers.splice(layerIndex, 1);
      }
      return;
    }
  }
};

export const applyAsoLayerSignalrUpdates = (
  layers: AsoScenarioMinLayerResponse[],
  updates: SignalrUpdate[],
): void => {
  updates.forEach(update => applyAsoLayerSignalrUpdate(layers, update));
};

export const handleAsoScenarioLayerChangeListener = async (
  asoScenarioId: number,
  api: AsoLayersCacheEntryApi,
): Promise<void> => {
  let isCacheActive = true;

  try {
    await api.cacheDataLoaded;

    const reloadLayers = () => {
      AsoCollection.scenariosMinLayersList(asoScenarioId)
        .then(({ data }) => {
          if (!isCacheActive) return;

          api.updateCachedData(draft => {
            draft.splice(0, draft.length, ...data.data);
          });
        })
        .catch(() => undefined);
    };

    const configurationListener = (signal: SignalAsoConfigurationChanged) => {
      if (
        signal.entityType !== 'AsoScenario' ||
        signal.entityId !== asoScenarioId ||
        signal.operationType !== ApiOperationType.Patch
      ) {
        return;
      }

      if (shouldReloadAsoLayersFromConfigurationSignalr(signal.updates)) {
        reloadLayers();
        return;
      }

      api.updateCachedData(draft => {
        applyAsoLayerSignalrUpdates(draft, signal.updates);
      });
    };

    const scopeListener = (signal: SignalAsoScenarioScopeChanged) => {
      if (
        signal.asoScenarioId !== asoScenarioId ||
        signal.operationType !== ApiOperationType.Patch ||
        !shouldReloadAsoLayersFromScopeSignalr(signal)
      ) {
        return;
      }

      reloadLayers();
    };

    signalrClient.on('ProjectConfigurationChanged', configurationListener);
    signalrClient.on('AsoScenarioScopeChanged', scopeListener);

    await api.cacheEntryRemoved;
    isCacheActive = false;
    signalrClient.off('ProjectConfigurationChanged', configurationListener);
    signalrClient.off('AsoScenarioScopeChanged', scopeListener);
  } catch {
    isCacheActive = false;
    // no-op in case cache entry was removed before data was loaded
  }
};
